import { OverviewViewV1Schema, type ThirtyDayPulseV1 } from "@fulcrum5/suite-contracts";
import { ArrowUpRight, RotateCw } from "lucide-react";
import { useCallback } from "react";
import { useOrganizationResource } from "./useOrganizationResource.ts";
import "./dashboardPages.css";

const OVERVIEW_PATH = (organizationId: string) => `/api/organizations/${organizationId}/overview`;
const numberFormatter = new Intl.NumberFormat("en-US");
const percentFormatter = new Intl.NumberFormat("en-US", {
  style: "percent",
  maximumFractionDigits: 1,
});
const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "F5";
}

function tokenCount(pulse: ThirtyDayPulseV1): number {
  return pulse.inputTokens + pulse.outputTokens + pulse.cachedTokens;
}

function Pulse({ pulse }: { readonly pulse: ThirtyDayPulseV1 }): JSX.Element {
  const cells = [
    ["Active members", numberFormatter.format(pulse.activeMembers)],
    ["Product sessions", numberFormatter.format(pulse.productSessions)],
    ["Success rate", pulse.successRate === null ? "Not available" : percentFormatter.format(pulse.successRate)],
    ["Tokens", numberFormatter.format(tokenCount(pulse))],
    ["Estimated cost", pulse.estimatedCostMicros === null
      ? "Not available"
      : currencyFormatter.format(pulse.estimatedCostMicros / 1_000_000)],
  ] as const;
  return (
    <section className="overview-page__pulse" aria-labelledby="overview-pulse-title">
      <div className="dashboard-section-heading">
        <div>
          <p className="dashboard-kicker">Activity</p>
          <h2 id="overview-pulse-title">30-day pulse</h2>
          <p className="overview-page__section-lede">
            How this organization is using the suite.
          </p>
        </div>
      </div>
      <dl className="overview-page__pulse-grid">
        {cells.map(([label, value]) => (
          <div className="overview-page__pulse-cell" key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export function OverviewPage(): JSX.Element {
  const resource = useOrganizationResource({
    path: OVERVIEW_PATH,
    responseSchema: OverviewViewV1Schema,
  });
  const retry = useCallback(() => { void resource.reload(); }, [resource]);

  if (resource.status === "loading" && !resource.data) {
    return <section className="platform-page dashboard-page"><p className="platform-loading" role="status">Loading overview.</p></section>;
  }
  if (!resource.data && resource.status === "idle") {
    return <section className="platform-page dashboard-page"><p className="platform-empty-state" role="status">An active organization is required to view this overview.</p></section>;
  }
  if (!resource.data) {
    return (
      <section className="platform-page dashboard-page">
        <div className="platform-notice platform-notice--error" role="alert">
          <p>{resource.message ?? "Overview could not be loaded."}</p>
          {resource.canRetry ? <button className="platform-button platform-button--secondary" onClick={retry} type="button">Retry overview</button> : null}
        </div>
      </section>
    );
  }

  const { account, organization, products, pulse } = resource.data;
  return (
    <section className="platform-page dashboard-page overview-page" aria-labelledby="overview-page-title">
      <header className="overview-page__hero">
        <div className="overview-page__hero-copy">
          <p className="overview-page__hero-eyebrow">Fulcrum5 · Workspace</p>
          <h1 id="overview-page-title">Overview</h1>
          <p className="overview-page__hero-tagline">
            A fixed point. A unified system. Everything aligned toward a single outcome.
          </p>
        </div>
        <div className="overview-page__hero-mark" aria-hidden="true">
          <span className="overview-page__hero-five">5</span>
          <span className="overview-page__hero-triangle" />
        </div>
      </header>

      {resource.status === "error" ? (
        <div className="platform-notice platform-notice--error dashboard-page__notice" role="alert">
          <p>{resource.message ?? "Overview could not be refreshed."}</p>
          {resource.canRetry ? <button className="platform-button platform-button--secondary" onClick={retry} type="button">Retry overview</button> : null}
        </div>
      ) : null}

      <section className="overview-page__account" aria-label="Active organization">
        <div className="overview-page__identity">
          {organization.logoUrl ? (
            <img alt={`${organization.name} logo`} className="overview-page__logo" src={organization.logoUrl} />
          ) : (
            <span aria-hidden="true" className="overview-page__logo overview-page__logo--fallback">{initials(organization.name)}</span>
          )}
          <div>
            <p className="dashboard-kicker">Active organization</p>
            <h2>{organization.name}</h2>
            <p className="overview-page__identity-meta">
              Client branding can layer on top — Fulcrum5 stays the foundation.
            </p>
          </div>
        </div>
        <dl className="overview-page__account-details">
          <div>
            <dt>Plan</dt>
            <dd>{account.planName}</dd>
          </div>
          <div>
            <dt>Access</dt>
            <dd>
              <span className="platform-badge" data-tone={account.accessStatus}>{account.accessLabel}</span>
            </dd>
          </div>
          <div>
            <dt>Rate</dt>
            <dd>{account.priceLabel}</dd>
          </div>
        </dl>
      </section>

      <section className="overview-page__products" aria-labelledby="overview-products-title">
        <div className="dashboard-section-heading">
          <div>
            <p className="dashboard-kicker">Suite</p>
            <h2 id="overview-products-title">Your products</h2>
            <p className="overview-page__section-lede">
              Source, Codify, Function, and Radius — one system, four surfaces.
            </p>
          </div>
        </div>
        <ul className="overview-page__product-list" aria-label="Available products">
          {products.map((product) => (
            <li className={`overview-page__product-card overview-page__product-card--${product.key}`} key={product.key}>
              <div className="overview-page__product-main">
                <img
                  className="overview-page__product-icon"
                  src={`/product-icons/${product.key}.svg`}
                  alt=""
                  width={48}
                  height={48}
                />
                <div className="overview-page__product-copy">
                  <h3>{product.name}</h3>
                  <p>{product.description}</p>
                </div>
              </div>
              <a
                className="overview-page__launch"
                aria-label={`Launch ${product.name}`}
                href={product.launchUrl}
                rel="noreferrer"
                target="_blank"
              >
                <span>Launch</span>
                <ArrowUpRight aria-hidden="true" size={16} strokeWidth={2} />
              </a>
            </li>
          ))}
        </ul>
      </section>

      {pulse ? <Pulse pulse={pulse} /> : null}
      {resource.status === "loading" ? (
        <p className="dashboard-page__refreshing" role="status">
          <RotateCw aria-hidden="true" className="platform-spin" size={15} /> Refreshing overview.
        </p>
      ) : null}
    </section>
  );
}
